import ApplicationController from '../application_controller';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import rrulePlugin from '@fullcalendar/rrule'
import interactionPlugin from '@fullcalendar/interaction';
import * as Turbo from '@hotwired/turbo';
import { get, patch } from '@rails/request.js';
import {
  computePosition,
  offset,
  flip,
  shift,
  arrow as arrowMw,
  autoUpdate
} from '@floating-ui/dom';
import { useClickOutside } from 'stimulus-use';
import TC, { TurboConfirm } from '@rolemodel/turbo-confirm';
import { RRule } from 'rrule';
import { DateTime } from 'luxon';
import tinycolor from 'tinycolor2';
import { UAParser } from 'ua-parser-js';

let cleanupAutoUpdate = null;
let activeEvent = null;

export default class extends ApplicationController {

  static targets = [
    'calendar',
    'popup',
    'popupContent',
    'popupTurboFrame',
    'arrow'
  ];

  static values = {
    channelViewUrl: String,
    newEventUrl: String,
    timeZone: String,
    fallbackEnabled: Boolean
  }

  connect() {
    TC.start();

    const parser = new UAParser();
    const OSName = parser.getOS().name;
    const isMobile = OSName === 'iOS' || OSName === 'Android';

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [
        dayGridPlugin,
        timeGridPlugin,
        listPlugin,
        interactionPlugin,
        rrulePlugin
      ],
      height: '100%',
      expandRows: true,
      initialView: isMobile ? 'timeGridDay' : 'dayGridMonth',
      editable: true,
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      nowIndicator: true,
      eventDidMount: (info) => {
        console.log('[Scheduler] - Event Content Mounted:', info);
        if (info.event.extendedProps?.artworkUrl) {
          info.el.insertAdjacentHTML('beforeend', `<img src="${info.event.extendedProps?.artworkUrl}" class="absolute top-0 left-0 h-full w-full object-cover" alt=""/><div style="background-color: ${info.event.backgroundColor};" class="absolute top-0 left-0 w-full h-full opacity-75"></div>`);
        }
      },
      eventDataTransform: (event) => {
        if (event.allDay) {
          // If an allDay event over multiple days crosses into the next day by hours
          // FullCalendar doesnot show this last day. So for visual purposes we add it back
          event.end = DateTime.fromISO(event.end).plus({ days: 1 }).ts;
        }
        return event;
      },
      events: async (info, successCallback, failureCallback) => {
        const response = await get(this.channelViewUrlValue, {
          credentials: 'include'
        });

        if (response.ok) {
          const channel = await response.json;

          const upcomingEvents = channel.included.filter(
            (included) => {
              return (
                included.type === 'event' && !included.attributes.accesscode_enabled
              );
            }
          );

          console.log('[Scheduler] Upcoming events:', upcomingEvents);

          successCallback(
            upcomingEvents && upcomingEvents.length ? upcomingEvents.map((event) => {
              const themeColor = tinycolor(event.attributes.color);
              const themeColorIsLight = themeColor.getLuminance() > 0.5;
              return {
                id: event.id,
                title: event.attributes.title,
                start: event.attributes.starts_at,
                end: event.attributes.ends_at,
                color: event.attributes.color,
                allDay: this.isAllDayEvent(event.attributes.starts_at, event.attributes.ends_at),
                display: 'block',
                resourceEditable: true,
                overlap: false,
                classNames: ['relative', 'overflow-hidden', `${themeColorIsLight ? '[&_*]:text-black' : '[&_*]:text-white'}`],
                extendedProps: {
                  adhoc: event.attributes.adhoc,
                  isLive: event.attributes.active,
                  artworkUrl: event.attributes.media?.artwork?.image?.medium || '',
                  scheduledRecording: event.attributes.scheduled_recording,
                  eventUrl: `/events/${event.id}`,
                  editUrl: `/events/${event.id}/edit?modal=true`,
                  popupUrl: `/events/${event.id}/popup`
                }
              }
            }) : []
          );
        } else {
          failureCallback(response.status);
        }
      },
      validRange: (nowDate) => {
        // Only allow events from today onwards
        return {
          start: nowDate
        };
      },
      eventClick: (info) => {
        console.log('[Scheduler] Event clicked:', info);
        this.openPopover(info.el, {
          event: info.event
        });
      },
      eventDrop: async (info) => {
        console.log('[Scheduler] Event dropped:', info);

        const eventUrl = info?.event?.extendedProps?.eventUrl;

        if (!eventUrl) return;

        const confirmEventDrop = new TurboConfirm();
        confirmEventDrop.confirmWithContent({
          '#confirm-title': 'Changing event date',
          '#confirm-body': 'Are you sure about this change?',
          '#confirm-accept': 'Yep!'
        }).then(async response => {
          if (response) {
            const result = await this.updateEventTimes({
              id: info.event.id,
              startsAt: info.event.start.toISOString(),
              endsAt: info.event.end.toISOString()
            });

            if (result.ok) {
              console.log('[Scheduler] Event dropped - Event updated');
            } else {
              console.log('[Scheduler] Event dropped - Event update failed', result.error);
              info.revert();
              const message = result.error?.errors?.join(", ") || result.error?.error || "Unknown error";
              const eventDropErrorAlert = new TurboConfirm({
                showConfirmCallback: element => {
                  element.showModal && element.showModal();
                  document.querySelector('.confirm-cancel').remove();
                }
              });
              eventDropErrorAlert.confirmWithContent({
                '#confirm-title': "Error updating event",
                '#confirm-body': `Error${result.status ? " " + result.status : ""}: ${message}`,
                '#confirm-accept': 'OK'
              });
            }
          } else {
            info.revert();
          }
        });
      },
      datesSet: () => {
        this.closePopup();
      },
      dateClick: (date) => {
        console.log('[Scheduler] Date clicked:', date);
        const newEventUrl = new URL(this.newEventUrlValue);
        newEventUrl.searchParams.append('date', date.date);
        if (date.view.type === 'timeGridWeek' || date.view.type === 'timeGridDay') {
          newEventUrl.searchParams.append('set_time', true);
          if (date.allDay) {
            newEventUrl.searchParams.append('all_day', true);
          }
        }
        Turbo.visit(newEventUrl, { frame: 'modal' })
      }
    });

    this.calendar.render();

    if (this.fallbackEnabledValue) {
      this.calendar.addEvent(
        {
          title: 'Fallback',
          editable: false,
          allDay: true,
          display: 'background',
          overlap: true,
          backgroundColor: 'rgba(218, 239, 247, 0.4)',
          textColor: '#47ADD8',
          rrule: {
            freq: RRule.DAILY,
            dtstart: new Date()
          },
          extendedProps: {
            artworkUrl: ''
          }
        }
      );
    }
  }

  isAllDayEvent(startsAt, endsAt) {
    let isAllDayEvent = false;
    const startDate = DateTime.fromISO(startsAt);
    const endDate = DateTime.fromISO(endsAt);
    const duration = endDate.diff(startDate, 'hours');
    if (startDate.equals(startDate.startOf('day')) && duration.values.hours >= 24) {
      isAllDayEvent = true
    }
    return isAllDayEvent;
  }

  refreshCalendarEvents() {
    console.log('[Scheduler] - Refreshing events');
    this.calendar.refetchEvents();

    if (this.hasPopupTurboFrameTarget && this.popupTarget.dataset.open && activeEvent) {
      this.popupTurboFrameTarget.reload();
      cleanupAutoUpdate?.();
    }
  }

  async openPopover(referenceEl, { event }) {
    if (activeEvent) {
      this.closePopup();
    }

    activeEvent = event;

    // Position and keep synced
    cleanupAutoUpdate?.();
    cleanupAutoUpdate = autoUpdate(referenceEl, this.popupTarget, () => {
      computePosition(referenceEl, this.popupTarget, {
        placement: 'top',
        middleware: [
          offset(8),
          flip({ fallbackPlacements: ['bottom', 'right', 'left'] }),
          shift({ padding: 8 }),
          arrowMw({ element: this.arrowTarget })
        ]
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(this.popupTarget.style, { left: `${x}px`, top: `${y}px` });

        // Arrow placement
        const { x: ax, y: ay } = middlewareData.arrow || {};
        const side = placement.split('-')[0];
        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right'
        }[side];

        // Reset all edges to avoid both top & bottom (or left & right) being set
        Object.assign(this.arrowTarget.style, { left: '', right: '', top: '', bottom: '' });

        if (side === 'top' || side === 'bottom') {
          if (ax != null) this.arrowTarget.style.left = `${ax}px`;
          this.arrowTarget.style[staticSide] = '-5px';
        } else {
          if (ay != null) this.arrowTarget.style.top = `${ay}px`;
          this.arrowTarget.style[staticSide] = '-5px';
        }
      });
    });

    const eventPopupUrl = activeEvent?.extendedProps?.popupUrl;
    if (eventPopupUrl) {
      this.popupTarget.dataset.open = 'true';
      this.popupTurboFrameTarget.src = eventPopupUrl;
    }
  }

  closePopup() {
    this.popupTurboFrameTarget.src = null;
    this.popupTarget.dataset.open = 'false';
    this.popupTarget.style.inset = '';
    
    if (this.hasPopupContentTarget) {
      this.popupContentTarget.remove();
    }

    activeEvent = null;
    cleanupAutoUpdate?.();
    cleanupAutoUpdate = null;
  }

  popupContentTargetConnected(element) {
    useClickOutside(this, { element: element });
  }

  onEscapeKeyClicked(event) {
    if (event.key === 'Escape') {
      this.closePopup();
    };
  }

  clickOutside(event) {
    const editingModal = event.target.closest('#turbo-modal');
    if (editingModal) return;
    
    if (!this.popupTarget.contains(event.target)) {
      this.closePopup();
    }
  }

  async updateEventTimes({ id, startsAt, endsAt }) {
    try {
      const response = await patch(`/events/${id}.json`, {
        contentType: "application/json",
        responseKind: "json",
        body: {
          event: {
            starts_at: startsAt,
            ends_at: endsAt
          }
        },
      });

      if (response.ok) {
        const data = await response.json;
        return { ok: true, status: response.statusCode, data };
      }

      try {
        const errorJson = await response.json;
        return { ok: false, status: response.statusCode, error: errorJson };
      } catch (_) {
        const errorText = await response.text;
        return { ok: false, status: response.statusCode, error: { error: errorText } };
      }
    } catch (e) {
      return { ok: false, status: null, error: { error: e.message } };
    }
  }
}